import { Grid, Typography, Box, Link } from "@mui/material"
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import styles from "../../Styles/ComponentStyles/InflationCalculator/InflationCalculator.module.scss"

const cpiCategories = [
    "Housing",
    "Transportations",
    "Food and Beverages",
    "Medical Care",
    "Education and Communication",
    "Recreation",
    "Apparel",
    "Other Goods and Services"
];

type CpiChangeInfo = { value: string; direction: string };

const PRE_ENERGY_SENTENCE_FIELDS: string[] = [
    "Food at home",
    "Meats, poultry, fish, and eggs",
];

const POST_ENERGY_SENTENCE_FIELDS: string[] = [
    "Shelter",
    "Medical care services",
    "Motor vehicle insurance",
    "Airline fares",
    "Motor vehicle maintenance and repair",
];

const buildOverallCpiSentence = (
    allItems: CpiChangeInfo | null,
    food: CpiChangeInfo | null,
    energy: CpiChangeInfo | null,
    blsSyncDate?: string
): string | null => {
    if (!allItems || !food || !energy) return null;
    return `The Consumer Price Index ${allItems.direction} ${allItems.value}% over the 12 months ending ${blsSyncDate ?? "May 2025"}, with food prices ${food.direction} ${food.value}% and energy prices ${energy.direction} by ${energy.value}%.`;
};

const buildEnergyCommoditySentences = (
    energyCommodities: CpiChangeInfo | null,
    gasoline: CpiChangeInfo | null,
    electricity: CpiChangeInfo | null
): string[] => {
    const sentences: string[] = [];
    if (energyCommodities && gasoline && electricity) {
        sentences.push(`Energy commodities ${energyCommodities.direction} ${energyCommodities.value}%, including a ${gasoline.direction} ${gasoline.value}% in gasoline (all types), while electricity ${electricity.direction} ${electricity.value}%.`);
    } else {
        if (energyCommodities) sentences.push(`Energy commodities ${energyCommodities.direction} ${energyCommodities.value}%.`);
        if (gasoline) sentences.push(`Gasoline (all types) ${gasoline.direction} ${gasoline.value}%.`);
        if (electricity) sentences.push(`Electricity ${electricity.direction} ${electricity.value}%.`);
    }
    return sentences;
};

const CpiCategoryData = ({ cpiData, blsSyncDate }: any) => {

    return (
        <Box className={styles.globalBlurWrapperForCPIComponent}>

            {/* Top header: title + subtitle */}
            <Typography className={styles.chartTitleText} marginBottom={"14px !important"}>
                Understanding Inflation & the Consumer Price Index (CPI)
            </Typography>
            <Typography fontSize={"14px"} color={"#364153"} mb={"28px"} lineHeight={1.6}>
                Inflation is typically measured using benchmarks like the Consumer Price Index (CPI), which tracks the average change over time in prices paid by urban consumers for a market basket of consumer goods and services.
            </Typography>

            <Grid container spacing={4}>

                {/* Left Column: Categories */}
                <Grid item xs={12} md={3}>
                    <Typography fontSize={"15px"} fontWeight={700} color={"#101828"} mb={"12px"}>
                        CPI Market Basket Categories
                    </Typography>
                    <Box component="ul" sx={{ pl: 2, m: 0 }}>
                        {cpiCategories.map((cat) => (
                            <li key={cat} style={{
                                marginBottom: 8,
                                fontSize: 14,
                                fontWeight: 400,
                                color: "#364153",
                                listStyleType: 'disc',
                            }}>
                                {cat}
                            </li>
                        ))}
                    </Box>
                </Grid>

                {/* Center Column: Price Changes */}
                <Grid item xs={12} md={4}>
                    <Typography fontSize={"15px"} fontWeight={700} color={"#101828"} mb={"12px"}>
                        {new Date().getFullYear()} Price Changes*
                    </Typography>
                    <Box>
                        {Array.isArray(cpiData) && cpiData
                            ?.filter((cat: any) => cat?.is_cpi_core_group === true)
                            .map((item: any) => {
                                const value = parseFloat(item.percent_change);
                                const isPositive = value > 0;
                                const isNegative = value < 0;

                                return (
                                    <Box
                                        key={item.bls_cpi_category_change_id}
                                        sx={{
                                            display: "flex",
                                            justifyContent: "space-between",
                                            alignItems: "center",
                                            background: isNegative ? "#F0FDF4" : "#FFF7ED",
                                            borderRadius: "6px",
                                            px: 1.5,
                                            py: 1,
                                            mb: 1,
                                        }}
                                    >
                                        {/* Icon + Label */}
                                        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                            {isNegative
                                                ? <TrendingDownIcon sx={{ fontSize: 18, color: "#059669" }} />
                                                : <TrendingUpIcon sx={{ fontSize: 18, color: "#DC6803" }} />
                                            }
                                            <Typography fontSize={"14px"} fontWeight={400} color={"#364153"}>
                                                {item.category_name}
                                            </Typography>
                                        </Box>

                                        {/* Value pill */}
                                        <Typography
                                            fontSize={"14px"}
                                            fontWeight={600}
                                            sx={{
                                                color: isNegative ? "#059669" : "#DC6803",
                                                minWidth: "48px",
                                                textAlign: "right"
                                            }}
                                        >
                                            {isPositive ? '+' : ''}{value.toFixed(1)}%
                                        </Typography>
                                    </Box>
                                );
                            })}
                    </Box>

                    {/* Caption */}
                    <Typography
                        fontSize={"12px"}
                        color={"#6A7282"}
                        mt={"10px"}
                        fontStyle={"italic"}
                        lineHeight={1.5}
                    >
                        *12 months percentage change. Consumer Price Index {blsSyncDate ? blsSyncDate : `${new Date().getFullYear()}`} not seasonally adjusted
                    </Typography>
                </Grid>

                {/* Right Column: Important Info */}
                <Grid item xs={12} md={5}>
                    <Typography fontSize={"15px"} fontWeight={700} color={"#101828"} mb={"10px"}>
                        Important Information
                    </Typography>
                    <Typography fontSize={"14px"} color={"#364153"} mb={"12px"} lineHeight={1.65}>
                        Inflation, although a blended average, MUST be itemized for a consumer to truly understand the
                        impact of inflation on their own budget.
                    </Typography>
                    <Typography fontSize={"14px"} color={"#364153"} mb={"12px"} lineHeight={1.65}>
                        {(() => {
                            const getValueAndDirection = (name: string) => {
                                const item = cpiData?.find((d: any) => d.category_name === name);
                                if (!item || item.percent_change === undefined || item.percent_change === null) return null;
                                const value = parseFloat(item.percent_change);
                                if (isNaN(value)) return null;
                                return {
                                    value: Math.abs(value).toFixed(1),
                                    direction: value > 0 ? 'increased' : value < 0 ? 'decreased' : 'remained flat'
                                };
                            };

                            const sentences: string[] = [];

                            const allItems = getValueAndDirection("All items");
                            const food = getValueAndDirection("Food");
                            const energy = getValueAndDirection("Energy");
                            const overallSentence = buildOverallCpiSentence(allItems, food, energy, blsSyncDate);
                            if (overallSentence) sentences.push(overallSentence);

                            PRE_ENERGY_SENTENCE_FIELDS.forEach((name) => {
                                const item = getValueAndDirection(name);
                                if (item) sentences.push(`${name} ${item.direction} ${item.value}%.`);
                            });

                            const energyCommodities = getValueAndDirection("Energy commodities");
                            const gasoline = getValueAndDirection("Gasoline (all types)");
                            const electricity = getValueAndDirection("Electricity");
                            sentences.push(...buildEnergyCommoditySentences(energyCommodities, gasoline, electricity));

                            POST_ENERGY_SENTENCE_FIELDS.forEach((name) => {
                                const item = getValueAndDirection(name);
                                if (item) sentences.push(`${name} ${item.direction} ${item.value}%.`);
                            });

                            return sentences.join(' ');
                        })()}
                    </Typography>

                    {/* Source link */}
                    <Link
                        href="https://www.bls.gov/news.release/cpi.t07.htm"
                        target="_blank"
                        fontSize={"12px"}
                        fontStyle={"italic"}
                        color={"#6A7282"}
                        sx={{ '&:hover': { color: "#3B78CE" }, textDecorationColor: "#6A7282" }}
                    >
                        Consumer Price Index Summary {blsSyncDate ? blsSyncDate : "4/12/2025"}
                    </Link>
                </Grid>

            </Grid>
        </Box>
    );
};

export default CpiCategoryData;